import { RouteObject } from "react-router-dom";
import Home from "../pages/home/Home";
import MainRoot from "../components/root/MainRoot";
import LangWrapper from "../components/root/LangWrapper";
import RootRedirect from "../components/root/RootRedirect";
import About from "../pages/about/About";
import Tariffs from "../pages/tariffs/Tariffs";
import Contact from "../pages/contact/Contact";
import Innovations from "../pages/innovations/Innovations";
import Stores from "../pages/stores/Stores";
import Login from "../pages/login/Login";
import Questions from "../pages/questions/Questions";
import ForbiddenProducts from "../pages/forbidden-products/ForbiddenProducts";
import OrderService from "../pages/order-service/OrderService";
import TransportConditions from "../pages/transport-conditions/TransportConditions";
import Privacy from "../pages/privacy/Privacy";
import UserAgreement from "../pages/user-agreement/UserAgreement";
import ForeignLocations from "../pages/admin-panels/foreign-locations/ForeignLocations";
import UserAdminPanel from "../components/root/UserAdminPanel";
import Notifications from "../pages/admin-panels/notifications/Notifications";
import MyPackages from "../pages/admin-panels/my-packages/MyPackages";
import SingleNotifications from "../pages/admin-panels/notifications/single-notification/SingleNotifications";
import Orders from "../pages/admin-panels/orders/Orders";
import Bucket from "../pages/admin-panels/bucket/Bucket";
import MyBalance from "../pages/admin-panels/my-balance/MyBalance";
import Calculator from "../pages/admin-panels/calculator/Calculator";
import Courier from "../pages/admin-panels/courier/Courier";
import MailPage from "../pages/admin-panels/mail_page/MailPage";
import Faq from "../pages/admin-panels/faq/Faq";
import UserRules from "../pages/admin-panels/user_rules/UserRules";
import OtpCode from "../pages/admin-panels/otp-code/OtpCode";
import ReferalAccounts from "../pages/admin-panels/referal-accounts/ReferalAccounts";
import MyAccount from "../pages/admin-panels/my-account/MyAccount";
import TrendyolOtp from "../pages/admin-panels/trendyol-otp/TrendyolOtp";
import NewReferalAccount from "../pages/admin-panels/newReferalAccount/NewReferalAccount";
import Promocode from "../pages/admin-panels/promo-kod/Promocode";
import Attorney from "../pages/admin-panels/attorney/Attorney";
import WatchPackages from "../pages/admin-panels/watch-packages/WatchPackages";
import DangerousPagkages from "../pages/dangerousPackages/DangerousPagkages";
import Corporate from "../pages/corporate/Corporate";
import Services from "../pages/services/Services";
import ServiceDetail from "../pages/services/service-detail/ServiceDetail";
import NewsDetail from "../pages/newsDetail/NewsDetail";
import PaymentSuccess from "../pages/payment-success/PaymentSuccess";
import PaymentFailed from "../pages/payment-failed/PaymentFailed";
import ErrorPage from "../pages/error/ErrorPage";
import ServerErrorPage from "../pages/serverErrorPage/ServerErrorPage";
import YpOrder from "../pages/admin-panels/ypOrder/YpOrder";
import { Wolt } from "../pages/admin-panels/wolt/Wolt";
import BankCards from "../pages/admin-panels/bank-cards/BankCards";
import SpecialOrder from "../pages/admin-panels/special-order/SpecialOrder";
import ServiceDebts from "../pages/admin-panels/service-debts/ServiceDebts";
import { ROUTE_SLUGS } from "../constants/route-slugs";

/** Registers the same element under all 3 language slugs for a given page */
const forAllLangs = (
  slugMap: Record<string, string>,
  element: RouteObject["element"]
): RouteObject[] => {
  const seen = new Set<string>();
  return Object.values(slugMap)
    .filter((slug) => {
      if (!slug || seen.has(slug)) return false;
      seen.add(slug);
      return true;
    })
    .map((slug) => ({ path: slug, element }));
};

/** Children of MainRoot — public pages with localized slugs */
const publicPageChildren: RouteObject[] = [
  { path: "", element: <Home /> },
  ...forAllLangs(ROUTE_SLUGS.about, <About />),
  ...forAllLangs(ROUTE_SLUGS.tariffs, <Tariffs />),
  // Tariff country detail: /az/tarifler/amerikadan-azerbaycana-kargo
  ...forAllLangs(ROUTE_SLUGS.tariffs, <Tariffs />).map((r) => ({
    path: `${r.path}/:countrySlug`,
    element: <Tariffs />,
  })),
  ...forAllLangs(ROUTE_SLUGS.contact, <Contact />),
  ...forAllLangs(ROUTE_SLUGS.innovations, <Innovations />),
  ...forAllLangs(ROUTE_SLUGS.stores, <Stores />),
  ...forAllLangs(ROUTE_SLUGS.questions, <Questions isAccount={false} />),
  ...forAllLangs(ROUTE_SLUGS.forbidden, <ForbiddenProducts />),
  ...forAllLangs(ROUTE_SLUGS.orderService, <OrderService />),
  ...forAllLangs(ROUTE_SLUGS.transportConditions, <TransportConditions />),
  ...forAllLangs(ROUTE_SLUGS.privacyPolicy, <Privacy />),
  ...forAllLangs(ROUTE_SLUGS.userAgreement, <UserAgreement />),
  ...forAllLangs(ROUTE_SLUGS.dangerousPackages, <DangerousPagkages />),
  ...forAllLangs(ROUTE_SLUGS.corporate, <Corporate />),
  ...forAllLangs(ROUTE_SLUGS.services, <Services />),
  ...forAllLangs(ROUTE_SLUGS.services, <Services />).map((r) => ({
    path: `${r.path}/:slug`,
    element: <ServiceDetail />,
  })),
  { path: ":category/:slug", element: <NewsDetail /> },
];


const ROUTES: RouteObject[] = [
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/:lang",
    element: <LangWrapper />,
    children: [
      {
        path: "",
        element: <MainRoot />,
        children: publicPageChildren,
      },
    ],
  },
  {
    path: "/auth",
    element: <Login />,
  },
  {
    path: "/payment-success",
    element: <PaymentSuccess />,
  },
  {
    path: "/payment-failed",
    element: <PaymentFailed />,
  },
  {
    path: "/account/",
    element: <UserAdminPanel />,
    children: [
      { path: "my-foriegn-locations", element: <ForeignLocations /> },
      { path: "order", element: <Orders /> },
      { path: "notifications", element: <Notifications /> },
      { path: "notifications/:id", element: <SingleNotifications /> },
      { path: "my-packages", element: <MyPackages /> },
      { path: "bucket", element: <Bucket /> },
      { path: "courier", element: <Courier /> },
      { path: "wolt", element: <Wolt /> },
      { path: "mail", element: <MailPage /> },
      { path: "my-balance", element: <MyBalance /> },
      { path: "bank-cards", element: <BankCards /> },
      { path: "calculator", element: <Calculator /> },
      { path: "faq", element: <Faq /> },
      { path: "user-rules", element: <UserRules /> },
      { path: "otp-code", element: <OtpCode /> },
      { path: "referal/referal_accounts", element: <ReferalAccounts /> },
      { path: "my-account", element: <MyAccount /> },
      { path: "trendyol-otp", element: <TrendyolOtp /> },
      { path: "referal/newreferal", element: <NewReferalAccount /> },
      { path: "promocode", element: <Promocode /> },
      { path: "attorney", element: <Attorney /> },
      { path: "referal/referal-packages/:id", element: <WatchPackages /> },
      { path: "ypOrder", element: <YpOrder /> },
      { path: "special-order", element: <SpecialOrder /> },
      { path: "service-debts", element: <ServiceDebts /> },
    ],
  },
  {
    path: "/server-error",
    element: <ServerErrorPage />,
  },
  {
    path: "*",
    element: <ErrorPage />,
  },
];

export default ROUTES;
